import React, { useState, useEffect } from 'react';
import {
  getAllCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
  subscribeToAllCompanies
} from '../services/vpsCompanies';
import './CompanyManagement.css';

// Default sound settings
const defaultSoundSettings = {
  enabled: true,
  volume: 60,
  beepCount: 3,
  pitch: 'medium',
  customSoundUrl: ''
};

const API_URL = process.env.REACT_APP_API_URL || 'https://multi-counter.bancheng.tech/api';

function CompanyManagement() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    allowGlobalAds: true,
    soundSettings: { ...defaultSoundSettings }
  });

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const data = await getAllCompanies();
        setCompanies(data);
        setLoading(false);
      } catch (err) {
        console.error('Error loading companies:', err);
        setLoading(false);
      }
    };

    loadCompanies();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToAllCompanies((data) => {
      setCompanies(data);
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCompany) {
        // Update existing company
        await updateCompany(editingCompany._id, {
          companyName: formData.companyName,
          allowGlobalAds: formData.allowGlobalAds,
          soundSettings: formData.soundSettings
        });
        alert('公司更新成功');
      } else {
        // Create new company
        await createCompany({
          companyName: formData.companyName,
          allowGlobalAds: formData.allowGlobalAds,
          soundSettings: formData.soundSettings
        });
        alert('公司建立成功');
      }

      // Reset form
      setShowForm(false);
      setEditingCompany(null);
      setFormData({
        companyName: '',
        allowGlobalAds: true,
        soundSettings: { ...defaultSoundSettings }
      });
    } catch (err) {
      console.error('Error saving company:', err);
      alert('操作失敗: ' + err.message);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      companyName: company.名稱,
      allowGlobalAds: company.廣告 !== undefined ? company.廣告 : true,
      soundSettings: company.soundSettings ? { ...defaultSoundSettings, ...company.soundSettings } : { ...defaultSoundSettings }
    });
    setShowForm(true);
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('確定要刪除此公司嗎？此操作無法復原。')) {
      return;
    }

    try {
      await deleteCompany(companyId);
      alert('公司刪除成功');
    } catch (err) {
      console.error('Error deleting company:', err);
      alert('刪除失敗: ' + err.message);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCompany(null);
    setFormData({
      companyName: '',
      allowGlobalAds: true,
      soundSettings: { ...defaultSoundSettings }
    });
  };

  // Helper to update sound settings
  const updateSoundSetting = (key, value) => {
    setFormData({
      ...formData,
      soundSettings: {
        ...formData.soundSettings,
        [key]: value
      }
    });
  };

  // Handle audio file upload
  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/x-m4a', 'audio/mp4'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|webm|m4a)$/i)) {
      alert('請上傳音檔格式 (MP3, WAV, OGG, WebM, M4A)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('檔案大小不能超過 5MB');
      return;
    }

    try {
      setUploading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('audio', file);

      const response = await fetch(`${API_URL}/uploads/sound`, {
        method: 'POST',
        body: formDataUpload
      });

      const result = await response.json();

      if (result.success) {
        updateSoundSetting('customSoundUrl', result.url);
        alert('音檔上傳成功！');
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('上傳失敗：' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // Play preview sound
  const playPreviewSound = () => {
    const url = formData.soundSettings.customSoundUrl;
    if (url) {
      const audio = new Audio(url);
      audio.volume = formData.soundSettings.volume / 100;
      audio.play().catch(err => {
        alert('無法播放音檔：' + err.message);
      });
    }
  };

  if (loading) {
    return (
      <div className="company-management">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="company-management">
      <div className="header">
        <h1>公司管理</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          ➕ 新增公司
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCompany ? '編輯公司' : '新增公司'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>公司名稱 *</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  required
                  placeholder="例: Justice Poker"
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.allowGlobalAds}
                    onChange={(e) => setFormData({ ...formData, allowGlobalAds: e.target.checked })}
                  />
                  允許廣告
                </label>
              </div>

              <div className="form-section">
                <h3>🔊 跳階段音效設定</h3>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.soundSettings.enabled}
                      onChange={(e) => updateSoundSetting('enabled', e.target.checked)}
                    />
                    啟用跳階段音效
                  </label>
                </div>

                {formData.soundSettings.enabled && (
                  <>
                    <div className="form-group">
                      <label>音量 ({formData.soundSettings.volume}%)</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={formData.soundSettings.volume}
                        onChange={(e) => updateSoundSetting('volume', parseInt(e.target.value))}
                        className="volume-slider"
                      />
                    </div>

                    <div className="form-group">
                      <label>嗶聲次數</label>
                      <select
                        value={formData.soundSettings.beepCount}
                        onChange={(e) => updateSoundSetting('beepCount', parseInt(e.target.value))}
                      >
                        <option value={1}>1 次</option>
                        <option value={2}>2 次</option>
                        <option value={3}>3 次</option>
                        <option value={4}>4 次</option>
                        <option value={5}>5 次</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>音調</label>
                      <select
                        value={formData.soundSettings.pitch}
                        onChange={(e) => updateSoundSetting('pitch', e.target.value)}
                      >
                        <option value="low">低音 (440Hz)</option>
                        <option value="medium">中音 (880Hz)</option>
                        <option value="high">高音 (1320Hz)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>自訂音檔</label>
                      <div className="sound-upload-section">
                        <div className="upload-row">
                          <input
                            type="file"
                            accept="audio/*"
                            onChange={handleAudioUpload}
                            disabled={uploading}
                            style={{ display: 'none' }}
                            id="audio-upload-company"
                          />
                          <label htmlFor="audio-upload-company" className="btn btn-secondary upload-btn">
                            {uploading ? '上傳中...' : '📁 選擇音檔'}
                          </label>
                          {formData.soundSettings.customSoundUrl && (
                            <>
                              <button type="button" className="btn btn-info preview-btn" onClick={playPreviewSound}>
                                🔊 試聽
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger clear-btn"
                                onClick={() => updateSoundSetting('customSoundUrl', '')}
                              >
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                        {formData.soundSettings.customSoundUrl && (
                          <div className="current-sound">
                            <small>目前音檔：{formData.soundSettings.customSoundUrl.split('/').pop()}</small>
                          </div>
                        )}
                        <div className="url-input-row">
                          <input
                            type="url"
                            value={formData.soundSettings.customSoundUrl}
                            onChange={(e) => updateSoundSetting('customSoundUrl', e.target.value)}
                            placeholder="或直接輸入音檔 URL"
                          />
                        </div>
                        <small className="help-text">支援 MP3, WAV, OGG, M4A 格式，最大 5MB。留空使用預設嗶聲</small>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCompany ? '更新' : '建立'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="companies-list">
        {companies.length === 0 ? (
          <div className="empty-state">
            <p>尚未建立任何公司</p>
            <p className="help-text">點擊上方「新增公司」按鈕開始建立</p>
          </div>
        ) : (
          <div className="companies-grid">
            {companies.map((company) => (
              <div key={company._id} className="company-card">
                <div className="company-header">
                  <h3>{company.名稱}</h3>
                  <span className={`status-badge ${company.廣告 ? 'active' : 'inactive'}`}>
                    {company.廣告 ? '允許廣告' : '不允許廣告'}
                  </span>
                </div>
                <div className="company-details">
                  <div className="detail-row">
                    <span className="label">公司 ID:</span>
                    <span className="value code">{company._id}</span>
                  </div>
                  {company.createdAt && (
                    <div className="detail-row">
                      <span className="label">建立時間:</span>
                      <span className="value">
                        {new Date(company.createdAt).toLocaleDateString('zh-TW')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="company-actions">
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => handleEdit(company)}
                  >
                    編輯
                  </button>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDelete(company._id)}
                  >
                    刪除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanyManagement;
